import React from "react";
import { AppBar, Box, IconButton, Stack, Toolbar } from "@mui/material";
import { HelpRounded, Menu as MenuIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { BrandMark } from "../BrandMark/BrandMark";
import { useUser } from "../../hooks/useUser";
import { shouldShowNotifications } from "../../utils";
import { NotificationControl } from "../Header/components/NotificationControl/NotificationControl";
import { UserControlV2 } from "../Header/components/UserControl/UserControlV2";
import { BugReportButton } from "../BugReport/BugReportButton";
import { ThemeToggle } from "./ThemeToggle";

interface TopBarProps {
  onMenu: () => void;
  helpLink: string;
}

// Secondary top bar: the main nav lives in the side rail, so this keeps only
// the secondary controls — mobile menu toggle, help, notifications and the
// account menu. Flush against the rail, sticky at the top of the content
// column, shadow only (no hard border).
export function TopBar({ onMenu, helpLink }: TopBarProps) {
  const { role, user } = useUser();
  const showNotifications = shouldShowNotifications(role, user);

  return (
    <>
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: "var(--topbar)",
        backgroundImage: "none",
        color: "var(--text)",
        boxShadow: "var(--sh-topbar)",
        // Fixed to the viewport top on mobile so it stays put while the page
        // scrolls (the rail is hidden at this width). Desktop keeps sticky —
        // the rail owns the left edge there, so a full-width fixed bar would
        // need extra offsetting.
        position: { xs: "fixed", md: "sticky" },
        top: 0,
        left: { xs: 0, md: "auto" },
        right: 0,
      }}
    >
      {/* 56px matches the rail header height so the two hairlines align */}
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 56 },
          px: { xs: 1.5, sm: 3 },
          justifyContent: "space-between",
        }}
      >
        {/* Left group: hamburger + logo icon (mobile only). Stays rendered
            even on desktop — an empty group keeps space-between anchoring the
            controls to the right; the rail owns the logo at that width. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            aria-label="Open navigation"
            onClick={onMenu}
            sx={{ display: { xs: "inline-flex", md: "none" }, color: "var(--text)" }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <BrandMark height={26} />
          </Box>
        </Box>

        {/* Right group: theme, help, notifications, account */}
        {user && (
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
            <BugReportButton />
            <ThemeToggle />
            <Link to={helpLink}>
              <IconButton aria-label="Help" color="primary">
                <HelpRounded />
              </IconButton>
            </Link>
            {showNotifications && <NotificationControl />}
            <UserControlV2 />
          </Stack>
        )}
      </Toolbar>
    </AppBar>
    {/* Mobile-only spacer: the fixed bar is out of flow, so reserve its
        height (matches the Toolbar) to keep content from sliding under it. */}
    <Box sx={{ display: { xs: "block", md: "none" }, height: 56 }} />
    </>
  );
}
